import React, { useEffect, useState } from 'react';
import apiClient from '../api/apiClient';
import { ENDPOINTS } from '../api/endpoints';
import { Link, useLocation } from 'react-router-dom';
import { apiGet, invalidateApiCache } from './apiCache';

const ThemeStored = () => {
    let Theme = localStorage.getItem('Theme');
    if (Theme) {
        return JSON.parse(Theme);
    }
    return 'light-theme';
};

export default function NavigationMenu() {
    const location = useLocation();
    const [IsLoggedIn, setIsLoggedIn] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile drawer open
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const stored = localStorage.getItem('sidebar_collapsed');
        return stored ? JSON.parse(stored) : false;
    });
    const [isResourcesOpen, setIsResourcesOpen] = useState(true); // Open by default for quick navigation
    const [isMobileResDrawerOpen, setIsMobileResDrawerOpen] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            setIsLoggedIn(true);
        } else {
            setIsLoggedIn(false);
        }
    }, []);

    const [Theme, setTheme] = useState(ThemeStored);
    
    const ChangeTheme = () => {
        const nextTheme = Theme === 'light-theme' ? 'dark-theme' : 'light-theme';
        setTheme(nextTheme);
        localStorage.setItem('Theme', JSON.stringify(nextTheme));
        window.dispatchEvent(new Event('themeChanged'));
    };

    // Dynamically adjust sidebar width CSS variable
    useEffect(() => {
        const width = isCollapsed ? '76px' : '260px';
        document.documentElement.style.setProperty('--sidebar-width', width);
        localStorage.setItem('sidebar_collapsed', JSON.stringify(isCollapsed));
    }, [isCollapsed]);

    const [OtherDropDown, setOtherDropDown] = useState({
        Name: '',
        Link: ''
    });

    const AddNewOtherDropDown = async (e) => {
        e.preventDefault();
        try {
            await apiClient.post(ENDPOINTS.OTHER_DROPDOWN.CREATE, { ...OtherDropDown })
                .then(result => {
                    invalidateApiCache('GetOtherDropDown');
                    alert('Quick site link added');
                    window.location.reload();
                })
                .catch(error => console.log(error));
        } catch (error) {
            console.log(error);
        }
    };

    const [AllOtherDropDown, setAllOtherDropDown] = useState([]);

    useEffect(() => {
        apiGet(ENDPOINTS.OTHER_DROPDOWN.GET_ALL)
            .then(data => setAllOtherDropDown(data || []))
            .catch(error => console.log(error));
    }, []);

    const DeleteOtherDropDown = async (id) => {
        apiClient.delete(ENDPOINTS.OTHER_DROPDOWN.DELETE(id))
            .then(result => {
                invalidateApiCache('GetOtherDropDown');
                window.location.reload();
            })
            .catch(error => console.log(error));
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    const isActive = (path) => {
        return location.pathname === path ? 'active' : '';
    };

    return (
        <>
            {/* Mobile Header Bar */}
            <div className="mobile-topbar">
                <button className="mobile-menu-toggle" onClick={toggleSidebar}>
                    <i className="bi bi-list"></i>
                </button>
                <span className="sidebar-logo-text" style={{ fontSize: '16px' }}><i className="bi bi-train-front-fill me-2 text-primary"></i>SECR Portal</span>
                <button className="btn btn-sm btn-outline-primary" onClick={ChangeTheme}>
                    <i className={`bi bi-${Theme === 'light-theme' ? 'moon-fill' : 'sun-fill'}`}></i>
                </button>
            </div>

            {/* Backdrop for Mobile */}
            {isSidebarOpen && <div className="sidebar-backdrop" onClick={closeSidebar}></div>}

            {/* Collapsible Sidebar */}
            <aside className={`sidebar ${isSidebarOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-logo d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2 overflow-hidden">
                        <i className="bi bi-train-front-fill text-primary fs-4"></i>
                        {!isCollapsed && <span className="sidebar-logo-text">CCC SECR BSP</span>}
                    </div>
                    {/* Sidebar Collapse Toggle Button (Desktop only) */}
                    <button 
                        className="btn btn-sm btn-link p-0 text-primary d-none d-lg-block"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
                    >
                        <i className={`bi bi-chevron-${isCollapsed ? 'right' : 'left'} fs-5`}></i>
                    </button>
                </div>

                <nav className="sidebar-nav">
                    <Link to='/' className={`sidebar-link ${isActive('/')}`} onClick={closeSidebar} title="Dashboard">
                        <span className="sidebar-link-content">
                            <i className="bi bi-speedometer2"></i>
                            {!isCollapsed && <span>Dashboard</span>}
                        </span>
                    </Link>

                    <Link to='/Contact' className={`sidebar-link ${isActive('/Contact')}`} onClick={closeSidebar} title="Staff Directory">
                        <span className="sidebar-link-content">
                            <i className="bi bi-people-fill"></i>
                            {!isCollapsed && <span>Staff Directory</span>}
                        </span>
                    </Link>

                    <Link to='/Notice' className={`sidebar-link ${isActive('/Notice')}`} onClick={closeSidebar} title="Notices">
                        <span className="sidebar-link-content">
                            <i className="bi bi-megaphone-fill"></i>
                            {!isCollapsed && <span>Notices</span>}
                        </span>
                    </Link>

                    {/* Resources logical grouping */}
                    <div>
                        <button 
                            className={`sidebar-link ${isResourcesOpen ? 'active' : ''}`}
                            onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                            title="Resources"
                        >
                            <span className="sidebar-link-content">
                                <i className="bi bi-folder-fill"></i>
                                {!isCollapsed && <span>Resources</span>}
                            </span>
                            {!isCollapsed && <i className={`bi bi-chevron-${isResourcesOpen ? 'up' : 'down'} fs-7`}></i>}
                        </button>
                        
                        {isResourcesOpen && !isCollapsed && (
                            <div className="sidebar-resources-group">
                                <Link to='/Books' className={`sidebar-resources-link ${isActive('/Books')}`} onClick={closeSidebar}>
                                    Books & Circulars
                                </Link>
                                <Link to='/Videos' className={`sidebar-resources-link ${isActive('/Videos')}`} onClick={closeSidebar}>
                                    Technical Videos
                                </Link>
                                <Link to='/Gallery' className={`sidebar-resources-link ${isActive('/Gallery')}`} onClick={closeSidebar}>
                                    Photo Gallery
                                </Link>
                                
                                <hr className="my-2 border-secondary" style={{ opacity: 0.1 }} />
                                
                                {AllOtherDropDown && AllOtherDropDown.map((Element, idx) => (
                                    <div className="d-flex align-items-center justify-content-between my-1 px-2" key={idx}>
                                        <a className="sidebar-resources-link flex-grow-1 p-0 py-1 text-truncate" href={Element.Link} target='_blank' rel="noreferrer" title={Element.Name}>
                                            {Element.Name}
                                        </a>
                                        {IsLoggedIn && (
                                            <button 
                                                className='btn btn-link text-danger p-0 ms-2' 
                                                onClick={() => DeleteOtherDropDown(Element._id)}
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {IsLoggedIn && (
                                    <button 
                                        type="button" 
                                        className="btn btn-sm btn-outline-primary mt-2 py-1 w-100"
                                        data-bs-toggle="modal" 
                                        data-bs-target="#AddNewOtherModal"
                                    >
                                        + Quick Link
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <Link to='/Quiz' className={`sidebar-link ${isActive('/Quiz')}`} onClick={closeSidebar} title="Quiz Module">
                        <span className="sidebar-link-content">
                            <i className="bi bi-question-circle-fill"></i>
                            {!isCollapsed && <span>Quiz Module</span>}
                        </span>
                    </Link>

                    <Link to='/Admin' className={`sidebar-link ${isActive('/Admin')}`} onClick={closeSidebar} title="Admin Center">
                        <span className="sidebar-link-content">
                            <i className="bi bi-shield-lock-fill"></i>
                            {!isCollapsed && <span>Admin Center</span>}
                        </span>
                    </Link>

                    <Link to='/PrivacyPolicy' className={`sidebar-link ${isActive('/PrivacyPolicy')}`} onClick={closeSidebar} title="Privacy Policy">
                        <span className="sidebar-link-content">
                            <i className="bi bi-shield-check"></i>
                            {!isCollapsed && <span>Privacy Policy</span>}
                        </span>
                    </Link>
                </nav>

                <div className="sidebar-footer">
                    <button className="sidebar-link" onClick={ChangeTheme} title={Theme === 'light-theme' ? 'Dark Theme' : 'Light Theme'}>
                        <span className="sidebar-link-content">
                            <i className={`bi bi-${Theme === 'light-theme' ? 'moon-fill' : 'sun-fill'}`}></i>
                            {!isCollapsed && <span>{Theme === 'light-theme' ? 'Dark Mode' : 'Light Mode'}</span>}
                        </span>
                    </button>

                    {IsLoggedIn ? (
                        <div className="text-xs text-success px-3 py-1 text-truncate">
                            <i className="bi bi-circle-fill me-2 fs-8"></i> {!isCollapsed && <span>Admin Session</span>}
                        </div>
                    ) : (
                        <Link to='/Admin' className={`btn btn-sm btn-outline-primary w-100 ${isCollapsed ? 'p-1' : 'px-2'}`} onClick={closeSidebar} title="Admin Login">
                            <i className="bi bi-box-arrow-in-right"></i> {!isCollapsed && <span className="ms-1">Login</span>}
                        </Link>
                    )}
                </div>
            </aside>

            {/* Quick Site Link Modal */}
            <div className="modal fade" id="AddNewOtherModal" tabIndex="-1" aria-labelledby="AddNewOtherModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <form onSubmit={AddNewOtherDropDown}>
                            <div className="modal-header">
                                <h5 className="modal-title" id="AddNewOtherModalLabel">Add Quick Link</h5>
                                <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <div className="form-group-custom">
                                    <label>Site Name</label>
                                    <input 
                                        className="form-control-custom"
                                        placeholder='Enter site name' 
                                        value={OtherDropDown.Name} 
                                        onChange={(e) => setOtherDropDown({ ...OtherDropDown, Name: e.target.value })} 
                                        type='text' 
                                        required
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label>Site URL</label>
                                    <input 
                                        className="form-control-custom"
                                        placeholder='https://example.com' 
                                        value={OtherDropDown.Link} 
                                        onChange={(e) => setOtherDropDown({ ...OtherDropDown, Link: e.target.value })} 
                                        type='url' 
                                        required
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                                <button type="submit" className="btn btn-primary-custom">Add Site</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Mobile Global Bottom Navigation Bar */}
            {!(location.pathname === '/Quiz' && localStorage.getItem('QuizQuestions')) && (
                <div className="mobile-global-bottom-bar">
                    <Link to="/" className={`mobile-global-bottom-btn ${isActive('/')}`} onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-speedometer2"></i>
                        <span>Dashboard</span>
                    </Link>
                    <Link to="/Contact" className={`mobile-global-bottom-btn ${isActive('/Contact')}`} onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-people-fill"></i>
                        <span>Staff</span>
                    </Link>
                    <Link to="/Notice" className={`mobile-global-bottom-btn ${isActive('/Notice')}`} onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-megaphone-fill"></i>
                        <span>Notices</span>
                    </Link>
                    <button 
                      className={`mobile-global-bottom-btn ${isMobileResDrawerOpen ? 'active' : ''}`}
                      onClick={() => setIsMobileResDrawerOpen(!isMobileResDrawerOpen)}
                    >
                        <i className="bi bi-folder-fill"></i>
                        <span>Resources</span>
                    </button>
                    <Link to="/Quiz" className={`mobile-global-bottom-btn ${isActive('/Quiz')}`} onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-question-circle-fill"></i>
                        <span>Assessment</span>
                    </Link>
                </div>
            )}

            {/* Mobile Resources Drawer Backdrop */}
            {isMobileResDrawerOpen && (
                <div className="mobile-global-backdrop" onClick={() => setIsMobileResDrawerOpen(false)}></div>
            )}

            {/* Mobile Resources Drawer Bottom Sheet */}
            <div className={`mobile-global-sheet ${isMobileResDrawerOpen ? 'open' : ''}`}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <span className="fw-bold text-main fs-6"><i className="bi bi-folder-fill text-primary me-2"></i>Resource Directory</span>
                    <button className="btn-close" onClick={() => setIsMobileResDrawerOpen(false)}></button>
                </div>
                
                <div className="d-flex flex-column gap-2 mb-4" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <Link to="/Books" className="p-3 border rounded-3 text-main text-decoration-none d-flex align-items-center gap-3 bg-light" onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-book text-primary fs-5"></i>
                        <span className="fw-semibold">Books & Circulars</span>
                    </Link>
                    <Link to="/Videos" className="p-3 border rounded-3 text-main text-decoration-none d-flex align-items-center gap-3 bg-light" onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-play-circle text-primary fs-5"></i>
                        <span className="fw-semibold">Technical Videos</span>
                    </Link>
                    <Link to="/Gallery" className="p-3 border rounded-3 text-main text-decoration-none d-flex align-items-center gap-3 bg-light" onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-images text-primary fs-5"></i>
                        <span className="fw-semibold">Photo Gallery</span>
                    </Link>
                    <Link to="/Admin" className="p-3 border rounded-3 text-main text-decoration-none d-flex align-items-center gap-3 bg-light" onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-shield-lock text-primary fs-5"></i>
                        <span className="fw-semibold">Admin Center</span>
                    </Link>
                    <Link to="/PrivacyPolicy" className="p-3 border rounded-3 text-main text-decoration-none d-flex align-items-center gap-3 bg-light" onClick={() => setIsMobileResDrawerOpen(false)}>
                        <i className="bi bi-shield-check text-primary fs-5"></i>
                        <span className="fw-semibold">DPDP Privacy Policy</span>
                    </Link>

                    {AllOtherDropDown && AllOtherDropDown.length > 0 && (
                        <div className="mt-3">
                            <span className="text-muted text-xs d-block mb-2">Quick External Links</span>
                            <div className="d-flex flex-column gap-2">
                                {AllOtherDropDown.map((Element, idx) => (
                                    <a key={idx} href={Element.Link} target="_blank" rel="noreferrer" className="p-2 border rounded-3 text-main text-decoration-none text-sm d-flex align-items-center justify-content-between bg-light">
                                        <span className="fw-medium text-truncate">{Element.Name}</span>
                                        <i className="bi bi-box-arrow-up-right text-muted text-xs"></i>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="d-flex justify-content-between align-items-center pt-3 border-top gap-3">
                    <button className="btn btn-outline-secondary flex-grow-1 py-2 text-sm" onClick={ChangeTheme}>
                        <i className={`bi bi-${Theme === 'light-theme' ? 'moon-fill' : 'sun-fill'} me-2`}></i>
                        {Theme === 'light-theme' ? 'Dark Mode' : 'Light Mode'}
                    </button>
                    {IsLoggedIn && (
                        <div className="text-xs text-success text-center">
                            <i className="bi bi-circle-fill me-1"></i> Admin Session
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
